// Update this to your real business email anytime.
export const BUSINESS_EMAIL = "Designs@mghstudios.com";

export const LOGO_URL = "/mgh-logo.png";
export const LOGO_LIGHT_URL = "/mgh-logo-light.png";

import {
  PenTool,
  CreditCard,
  Brush,
  BookOpen,
  Baby,
  Palette,
  Shapes,
  Sparkles,
} from "lucide-react";

export const SERVICES = [
  {
    id: "logos",
    title: "Business Logos",
    blurb:
      "Distinctive, memorable logo marks that capture the soul of your brand and scale everywhere.",
    keywords: ["logo", "brand", "branding", "identity", "mark", "business"],
    color: "red",
    icon: PenTool,
    featured: true,
  },
  {
    id: "cards",
    title: "Business Cards",
    blurb:
      "Print-ready business card designs that make a powerful first impression in the palm of a hand.",
    keywords: ["business card", "cards", "print", "stationery", "branding"],
    color: "blue",
    icon: CreditCard,
  },
  {
    id: "abstract",
    title: "Abstract Art",
    blurb:
      "Original abstract art — geometric abstraction or expressive gestural pieces — made to command attention.",
    keywords: ["abstract", "art", "geometric", "expressionism", "canvas", "painting"],
    color: "yellow",
    icon: Shapes,
  },
  {
    id: "portraits",
    title: "Commission Portraits",
    blurb:
      "Hand-crafted commissioned portraits — pencil, ink, or color — drawn from your favorite photos.",
    keywords: ["portrait", "portraits", "commission", "drawing", "sketch", "art"],
    color: "yellow",
    icon: Brush,
    featured: true,
  },
  {
    id: "comics",
    title: "Comic Books",
    blurb:
      "Full comic book creation — characters, panels, lettering and covers that leap off the page.",
    keywords: ["comic", "comics", "comic book", "illustration", "graphic novel"],
    color: "blue",
    icon: BookOpen,
  },
  {
    id: "childrens",
    title: "Children's Books",
    blurb:
      "Warm, whimsical illustrations and layouts that bring children's stories to life.",
    keywords: ["children", "kids", "childrens book", "book", "illustration", "story"],
    color: "red",
    icon: Baby,
  },
  {
    id: "illustration",
    title: "Custom Illustration",
    blurb:
      "Bold custom illustrations for any project — editorial, packaging, posters and more.",
    keywords: ["illustration", "art", "drawing", "custom", "poster"],
    color: "yellow",
    icon: Palette,
  },
  {
    id: "special",
    title: "Special Projects",
    blurb:
      "One-of-a-kind commissions — portrait illusions, custom 3D design, and full card game creation.",
    keywords: ["special", "3d", "portrait illusion", "card game", "projects", "custom"],
    color: "blue",
    icon: Sparkles,
  },
];

export const LOGO_TYPES = [
  { name: "Wordmark", desc: "A font-driven logo built around your business name alone." },
  { name: "Lettermark", desc: "A clean monogram made from your brand's initials." },
  { name: "Letterform", desc: "A single stylized letter that becomes the icon." },
  { name: "Pictorial Mark", desc: "A recognizable icon or image-based symbol.", price: 69 },
  { name: "Abstract Mark", desc: "A unique geometric form that represents your brand.", price: 69 },
  { name: "Mascot", desc: "An illustrated character that gives your brand personality.", price: 99 },
  { name: "Emblem", desc: "Your name enclosed in a badge, crest, or seal.", price: 89 },
  { name: "Combination Mark", desc: "A symbol paired together with your business name.", price: 69 },
  { name: "Dynamic Mark", desc: "A flexible logo that adapts across different contexts.", price: 99 },
];

export const PORTRAIT_MEDIUMS = [
  {
    name: "Oil Paint",
    icon: "Brush",
    sizeLabel: "Canvas Sizes",
    desc: "Rich, textured oil portraits hand-painted on quality canvas.",
    sizes: [
      { size: "11x14", price: 449 },
      { size: "16x20", price: 699 },
    ],
    note: "*Oil paintings require a 7-day drying period before shipping.",
  },
  {
    name: "Soft Pastel",
    icon: "Palette",
    sizeLabel: "Paper Sizes",
    paper: "PastelMat Paper",
    desc: "Soft, vibrant pastel portraits with a smooth, blended finish.",
    sizes: [
      { size: "6x9", price: 229 },
      { size: "11x14", price: 349 },
      { size: "16x20", price: 529 },
    ],
  },
  {
    name: "Charcoal",
    icon: "Pencil",
    sizeLabel: "Paper Sizes",
    paper: "Acid-free Paper",
    desc: "Classic black-and-white charcoal portraits full of depth and contrast.",
    sizes: [
      { size: "6x9", price: 189 },
      { size: "11x14", price: 299 },
      { size: "16x20", price: 369 },
    ],
  },
];

export const CARD_QUANTITIES = [25, 50, 100, 250, 500, 1000];

export const BUSINESS_CARD_STYLES = [
  {
    name: "Rectangular Style",
    desc: "The classic 3.5\" × 2\" card — timeless, professional, and ready for any wallet.",
    prices: { 25: 49, 50: 69, 100: 89, 250: 129, 500: 169, 1000: 199 },
  },
  {
    name: "Square Style",
    desc: "A bold, modern square card that stands out and makes a memorable impression.",
    prices: { 25: 59, 50: 89, 100: 99, 250: 149, 500: 189, 1000: 229 },
  },
];

export const PORTFOLIO = [
  {
    title: "Abstract Art",
    tag: "Abstract",
    img: "https://customer-assets.emergentagent.com/job_hands-creative/artifacts/2w8yvx8k_0973A50B-84E4-4C72-95AD-485083B9107C.png",
  },
  {
    title: "Comic Book Design",
    tag: "Comics",
    img: "https://customer-assets.emergentagent.com/job_hands-creative/artifacts/gazx6a93_F47270FB-6090-4A83-8281-550C1E3D2B4A.png",
  },
  {
    title: "Portrait Commission",
    tag: "Portraits",
    img: "https://customer-assets.emergentagent.com/job_hands-creative/artifacts/p9o6292g_ED21DFC0-E527-486A-88D4-FBABC9871606.png",
  },
  {
    title: "Business Card Design",
    tag: "Business",
    img: "https://customer-assets.emergentagent.com/job_hands-creative/artifacts/hdqn9rhq_51899F08-4F11-4552-A507-2C091DDF520D.png",
  },
  {
    title: "Digital Art",
    tag: "Illustrations",
    img: "https://customer-assets.emergentagent.com/job_hands-creative/artifacts/acn3wbyv_IMG_8447.png",
  },
  {
    title: "Portrait Illusions",
    tag: "Special Projects",
    img: "https://customer-assets.emergentagent.com/job_hands-creative/artifacts/9tunos1j_C7B72BA9-4E7D-41B9-9CA6-24E879F3AF31.jpeg",
  },
];
